import type {
  DisclosureNewsApiLabel,
  DisclosureNewsApiResponse,
  DisclosureNewsResult,
  DisclosureReportLinkApiResponse,
} from '@app-types/instructionsApi';
import { disclosuresClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

export const INSTRUCTIONS_API_FETCH_SIZE = 100;
export const INSTRUCTIONS_API_SCROLL_POS = 0;

export interface FetchDisclosureNewsParams {
  ticker: string;
  mkt: string;
  label: DisclosureNewsApiLabel;
  /** Inclusive disclosure range in Japan calendar format (`YYYYMMDD`). */
  from: string;
  /** Inclusive disclosure range in Japan calendar format (`YYYYMMDD`). */
  to: string;
  fetchSize?: number;
  scrollPos?: number;
}

export interface FetchDisclosureReportLinkParams {
  ticker: string;
  mkt: string;
  pnac: string;
  createdAt: string;
}

const normalizeAvailableTotal = (
  value: unknown,
  availableRows: number,
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return availableRows;
  }

  return Math.min(Math.floor(value), availableRows);
};

/** Fetches one complete client-pagination batch for the selected disclosure tab. */
export const fetchDisclosureNews = async (
  params: FetchDisclosureNewsParams,
): Promise<DisclosureNewsResult> => {
  try {
    const response = await disclosuresClient.get<DisclosureNewsApiResponse>('/news', {
      params: {
        ticker: params.ticker,
        mkt: params.mkt,
        label: params.label,
        from: params.from,
        to: params.to,
        fetchSize: params.fetchSize ?? INSTRUCTIONS_API_FETCH_SIZE,
        scrollPos: params.scrollPos ?? INSTRUCTIONS_API_SCROLL_POS,
      },
    });

    const body = response.data;
    if (body.status !== 0 || !Array.isArray(body.data)) {
      throwDataApiError(
        body,
        response.status,
        'Timely disclosure news response is invalid.',
      );
    }

    return {
      rows: body.data,
      total: normalizeAvailableTotal(body.total, body.data.length),
    };
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

const normalizeReportUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;

  const reportUrl = value.trim();
  try {
    const parsed = new URL(reportUrl);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? reportUrl
      : null;
  } catch {
    return null;
  }
};

/** Resolves the selected disclosure to the backend-provided report URL. */
export const fetchDisclosureReportLink = async (
  params: FetchDisclosureReportLinkParams,
): Promise<string> => {
  try {
    const response = await disclosuresClient.get<DisclosureReportLinkApiResponse>(
      '/news/report-link',
      {
        params: {
          ticker: params.ticker,
          mkt: params.mkt,
          pnac: params.pnac,
          createdAt: params.createdAt,
        },
      },
    );

    const body = response.data;
    const reportUrl = body.status === 0
      ? normalizeReportUrl(body.data?.reportUrl)
      : null;

    if (!reportUrl) {
      return throwDataApiError(
        body,
        response.status,
        'Timely disclosure report link response is invalid.',
      );
    }

    return reportUrl;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};
